import { useEffect, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

import { useAccountSetting } from '@/hooks/useSetting'
import type { AccountSettings } from '@/types/account'
import type { AccountPhoneNumber } from '@/types/account'
import { AccountLayout } from './-layout'
import { Plus, Trash2, Phone, Globe, Star, EyeOff } from 'lucide-react'
import { parsePhoneNumberFromString } from 'libphonenumber-js'
import {
  useVerifiedPhoneNumbersQuery,
  useRemoveVerifiedPhoneNumber,
} from '@/hooks/useVerifiedPhoneNumbers'
import { VerifyPhoneNumberDialog } from '@/components/phone/VerifyPhoneNumberDialog'
import { PhoneNumber } from '@/components/ui/phone-number'
import { Flag } from '@/components/Flag'
import { useQuery } from '@tanstack/react-query'
import { accountQueries } from '@/services/accountApi'
import { useUpdateCurrency } from '@/hooks/useAccount'
import { useCurrency } from '@/hooks/useCurrency'
import { CURRENCIES } from '@/config/currency'

export const Route = createFileRoute('/$lang/account/settings')({
  component: SettingsRoute,
})

interface PhoneNumberDetails {
  countryCode?: string
  numberType?: string
}

function getPhoneNumberDetails(phoneNumber: string): PhoneNumberDetails {
  const parsed = parsePhoneNumberFromString(phoneNumber)
  if (!parsed) return {}

  const countryCode = parsed.country
  const numberType = parsed.getType()

  return {
    countryCode,
    numberType: numberType || undefined,
  }
}

function SettingsRoute() {
  const { t } = useTranslation(['app', 'common', 'tools'])
  const [isVerifyDialogOpen, setIsVerifyDialogOpen] = useState(false)
  const [confirmingDeleteNumber, setConfirmingDeleteNumber] = useState<string | null>(null)
  const removePhoneNumberMutation = useRemoveVerifiedPhoneNumber()

  const {
    data: phoneNumbersData,
    isLoading: isLoadingPhoneNumbers,
    error: phoneNumbersError,
    refresh: refreshPhoneNumbers,
  } = useVerifiedPhoneNumbersQuery()

  const { value: callerIdValue, setAccountDefault: setCallerIdDefault, isUpdating } = useAccountSetting('call:caller_id_mode')

  const isDefaultAny = callerIdValue === 'cheapest'
  const isDefaultAnonymous = callerIdValue === 'anonymous'

  const handleMakeDefault = async (phoneNumber: string) => {
    try {
      await setCallerIdDefault(phoneNumber)
      toast.success(t('settings.saved'))
    } catch (err: any) {
      toast.error(err?.message ?? t('settings.saveFailed'))
    }
  }

  const handleMakeAnyDefault = async () => {
    try {
      await setCallerIdDefault('cheapest')
      toast.success(t('settings.saved'))
    } catch (err: any) {
      toast.error(err?.message ?? t('settings.saveFailed'))
    }
  }

  const handleMakeAnonymousDefault = async () => {
    try {
      await setCallerIdDefault('anonymous')
      toast.success(t('settings.saved'))
    } catch (err: any) {
      toast.error(err?.message ?? t('settings.saveFailed'))
    }
  }

  useEffect(() => {
    if (phoneNumbersError) toast.error(phoneNumbersError.message)
  }, [phoneNumbersError])

  return (
    <AccountLayout>
      <div className="space-y-6">

        {/* Verified Caller IDs Card */}
        <Card flush>
          <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle>{t('phoneVerification.verifiedCallerIds')}</CardTitle>
              <CardDescription>{t('phoneVerification.verifiedCallerIdsDescription')}</CardDescription>
            </div>
            <Button className="self-start sm:self-auto shrink-0" onClick={() => setIsVerifyDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              {t('phoneVerification.addNumber')}
            </Button>
          </CardHeader>
          <CardContent>
            {isLoadingPhoneNumbers ? (
              <div className="space-y-3">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className="flex items-center justify-between rounded-lg bg-muted/50 p-4">
                    <div className="flex items-center gap-3">
                      <Skeleton className="h-10 w-10 rounded-full" />
                      <div className="space-y-1.5">
                        <Skeleton className="h-4 w-36" />
                        <Skeleton className="h-3 w-20" />
                      </div>
                    </div>
                    <Skeleton className="h-5 w-16 rounded-md" />
                  </div>
                ))}
              </div>
            ) : phoneNumbersError ? (
              <div className="rounded-lg border border-dashed border-border/60 bg-muted/20 py-10 text-center">
                <Button variant="outline" size="sm" onClick={refreshPhoneNumbers}>
                  {t('common.retry')}
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Anonymous - Global Number Pool option */}
                <div
                  className={`flex items-center justify-between rounded-lg p-4 ${
                    isDefaultAnonymous ? 'bg-primary/10 border border-primary/30' : 'bg-muted/50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary text-lg">
                      <EyeOff className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="font-semibold">{t('phoneVerification.anonymousOption')}</p>
                      <p className="text-xs text-muted-foreground">
                        {t('phoneVerification.anonymousDescription')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {isDefaultAnonymous && (
                      <Badge variant="default" className="text-xs font-bold">
                        <Star className="h-3 w-3 mr-1" />
                        {t('phoneVerification.default')}
                      </Badge>
                    )}
                    {!isDefaultAnonymous && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleMakeAnonymousDefault}
                        disabled={isUpdating}
                      >
                        {t('phoneVerification.makeDefault')}
                      </Button>
                    )}
                  </div>
                </div>

                {/* Any verified caller ID, else global pool if surcharge */}
                <div
                  className={`flex items-center justify-between rounded-lg p-4 ${
                    isDefaultAny ? 'bg-primary/10 border border-primary/30' : 'bg-muted/50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary text-lg">
                      <Globe className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="font-semibold">{t('phoneVerification.anyOption')}</p>
                      <p className="text-xs text-muted-foreground">
                        {t('phoneVerification.anyDescription')}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {isDefaultAny && (
                      <Badge variant="default" className="text-xs font-bold">
                        <Star className="h-3 w-3 mr-1" />
                        {t('phoneVerification.default')}
                      </Badge>
                    )}
                    {!isDefaultAny && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleMakeAnyDefault}
                        disabled={isUpdating}
                      >
                        {t('phoneVerification.makeDefault')}
                      </Button>
                    )}
                  </div>
                </div>

                {/* Verified numbers list */}
                {phoneNumbersData && phoneNumbersData.items.length > 0 ? (
                  phoneNumbersData.items.map((phone: AccountPhoneNumber) => {
                    const details = getPhoneNumberDetails(phone.phoneNumber)
                    const isDefault = callerIdValue === phone.phoneNumber
                    const canSelect = phone.verified

                    return (
                      <div
                        key={phone.id}
                        className={`flex items-center justify-between rounded-lg p-4 ${
                          isDefault ? 'bg-primary/10 border border-primary/30' : 'bg-muted/50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                            {details.countryCode ? (
                              <Flag countryCode={details.countryCode} size="md" />
                            ) : (
                              <Phone className="h-4 w-4" />
                            )}
                          </div>
                          <div>
                            <PhoneNumber number={phone.phoneNumber} className="text-sm font-semibold" />
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <span>
                                {phone.createdAt
                                  ? t('phoneVerification.verifiedOn', { date: new Date(phone.createdAt).toLocaleDateString() })
                                  : ''}
                              </span>
                              {details.numberType && (
                                <>
                                  <span>·</span>
                                  <Badge variant="outline" className="text-[10px] px-1 py-0 h-4">
                                    {t(`tools.phoneValidator.result.types.${details.numberType}`, { defaultValue: details.numberType })}
                                  </Badge>
                                </>
                              )}
                              {phone.verified && (
                                <>
                                  <span>·</span>
                                  <span className="text-amber-600 dark:text-amber-400">{t('phoneVerification.mayIncurSurcharge')}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          {isDefault && (
                            <Badge variant="default" className="text-xs font-bold">
                              <Star className="h-3 w-3 mr-1" />
                              {t('phoneVerification.default')}
                            </Badge>
                          )}
                          {canSelect && !isDefault && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleMakeDefault(phone.phoneNumber)}
                              disabled={isUpdating}
                            >
                              {t('phoneVerification.makeDefault')}
                            </Button>
                          )}
                          <Badge
                            variant={phone.verified ? 'default' : 'secondary'}
                            className="text-xs font-bold"
                          >
                            {phone.verified
                              ? t('phoneVerification.statusVerified')
                              : t('phoneVerification.statusPending')}
                          </Badge>
                          {confirmingDeleteNumber === phone.phoneNumber ? (
                            <>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setConfirmingDeleteNumber(null)}
                                disabled={removePhoneNumberMutation.isPending}
                              >
                                {t('common.cancel')}
                              </Button>
                              <Button
                                variant="destructive"
                                size="sm"
                                disabled={removePhoneNumberMutation.isPending}
                                onClick={() => {
                                  removePhoneNumberMutation.mutate(phone.phoneNumber, {
                                    onSuccess: () => {
                                      setConfirmingDeleteNumber(null)
                                      toast.success(t('phoneVerification.removed'))
                                    },
                                    onError: (err: any) =>
                                      toast.error(err?.message ?? t('phoneVerification.removeFailed')),
                                  })
                                }}
                              >
                                {removePhoneNumberMutation.isPending ? t('common.loading') : t('phoneVerification.remove')}
                              </Button>
                            </>
                          ) : (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-destructive hover:bg-destructive/10 hover:text-destructive"
                              onClick={() => setConfirmingDeleteNumber(phone.phoneNumber)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    )
                  })
                ) : (
                  <div className="space-y-4">
                    <div className="rounded-lg border border-dashed border-border/60 bg-muted/20 py-12 text-center text-sm text-muted-foreground">
                      {t('phoneVerification.noNumbers')}
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Theme Card */}
        <Card flush>
          <CardHeader>
            <CardTitle>{t('settings.theme.label')}</CardTitle>
            <CardDescription>{t('settings.theme.description')}</CardDescription>
          </CardHeader>
          <CardContent>
            <ThemeSetting />
          </CardContent>
        </Card>

        {/* Currency Card */}
        <Card flush>
          <CardHeader>
            <CardTitle>{t('settings.currency.label', 'Currency')}</CardTitle>
            <CardDescription>{t('settings.currency.description', 'The currency used for billing and balance display.')}</CardDescription>
          </CardHeader>
          <CardContent>
            <CurrencySetting />
          </CardContent>
        </Card>

      </div>

      <VerifyPhoneNumberDialog
        open={isVerifyDialogOpen}
        onOpenChange={setIsVerifyDialogOpen}
      />
    </AccountLayout>
  )
}

// Theme setting component
function ThemeSetting() {
  const { t } = useTranslation(['app', 'common', 'tools'])
  const { value: theme, setAccountDefault: setThemeDefault, isLoading } = useAccountSetting('ui:theme')

  const handleChange = async (value: AccountSettings['ui:theme']) => {
    try {
      await setThemeDefault(value)
      toast.success(t('settings.saved'))
    } catch (err: any) {
      toast.error(err?.message ?? t('settings.saveFailed'))
    }
  }

  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <div className="space-y-0.5">
        <Label htmlFor="setting-theme">{t('settings.theme.label')}</Label>
        <p className="text-sm text-muted-foreground">{t('settings.theme.description')}</p>
      </div>
      {isLoading ? (
        <Skeleton className="h-9 w-36" />
      ) : (
        <Select
          value={theme}
          onValueChange={handleChange}
        >
          <SelectTrigger id="setting-theme" className="w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="light">{t('settings.theme.light')}</SelectItem>
            <SelectItem value="dark">{t('settings.theme.dark')}</SelectItem>
            <SelectItem value="system">{t('settings.theme.system')}</SelectItem>
          </SelectContent>
        </Select>
      )}
    </div>
  )
}

// Currency setting component
function CurrencySetting() {
  const { t } = useTranslation(['app', 'common'])
  const { currency, setCurrency } = useCurrency()
  const { data: accountData, isLoading } = useQuery(accountQueries.me())
  const updateCurrencyMutation = useUpdateCurrency()
  const isCurrencyLocked = accountData?.account.currencyLocked ?? false

  const handleChange = async (value: string) => {
    try {
      await updateCurrencyMutation.mutateAsync(value)
      setCurrency(value)
      toast.success(t('settings.saved'))
    } catch (err: any) {
      toast.error(err?.message ?? t('settings.saveFailed'))
    }
  }

  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
      <div className="space-y-0.5">
        <Label htmlFor="setting-currency">{t('settings.currency.label', 'Currency')}</Label>
        <p className="text-sm text-muted-foreground">
          {isCurrencyLocked
            ? t('settings.currency.lockedDescription', 'Currency cannot be changed after your first transaction.')
            : t('settings.currency.description', 'The currency used for billing and balance display.')}
        </p>
      </div>
      {isLoading ? (
        <Skeleton className="h-9 w-36" />
      ) : (
        <Select
          value={currency.code}
          onValueChange={handleChange}
          disabled={isCurrencyLocked || updateCurrencyMutation.isPending}
        >
          <SelectTrigger id="setting-currency" className="w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.values(CURRENCIES).map((c) => (
              <SelectItem key={c.code} value={c.code}>
                {c.symbol} {c.code} — {c.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  )
}
